// screens/VoiceCallScreen.tsx — C4 / V28 S1: full-screen Il Prof voice-call surface.
//
// Maps verbatim to the prototype `docs/specs/v28-prototype/screens/voicecall.html`:
//   - top chrome: hamburger + Prof identity + live in-call status (rec dot + MM:SS),
//   - a live streaming transcript thread (you-bubbles + Prof prose, trailing caret
//     on the in-flight Prof turn),
//   - a bottom control bar: <VoiceOrb size="sm"/> + status + a compact circular Stop
//     (plus optional Interrompi / Riprova controls).
//
// V28 S1 — PRESENTATIONAL ONLY. This screen no longer runs its own
// `useQwenRealtimeVoice` hook. It is rendered as an OVERLAY by ProfScreen, which
// owns ALL the voice orchestration + guards (app-complaint boundary, visual-aid
// injection, persona-boundary, topic-switch, student-correction, persist). The
// host feeds this surface the live transcript node, the status, the elapsed
// seconds, and the control callbacks. Keeping the engine in ProfScreen means
// every voice guard + its ~30 tests stay exactly where they already live and
// pass — this file only paints the UI.
//
// LIGHT-ONLY: all colour comes from tokens via useTokens() (tokens.mode resolves
// "light"); values not in tokens (rec rose-dot, violet stop ring/shadow) are
// lifted verbatim from the mockup, matching VoiceOrb's light-only approach.
//
// MOTION: the rec-dot blink + the streaming trailing caret are gated by
// useReducedMotion() (state/use-reduced-motion.ts) — when reduced they render
// static (dot fully opaque, caret hidden). The keyframes (rec-blink / caret-blink)
// live in styles/global.css. The orb's own breathe/halo gating lives in VoiceOrb.

import type { CSSProperties, JSX, ReactNode } from 'react';
import { Screen } from '../components/Screen';
import { VoiceOrb } from '../components/VoiceOrb';
import { InlineImage } from '../components/InlineImage';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';

// Voice-visuals rebuild P0a — the SINGLE current visual set rendered in the
// dedicated slot. It is the host's source of truth: REPLACED (not appended) each
// turn, and the SAME set feeds the model's currentVisibleImages.
export interface VoiceCallVisualSign {
  id: string;
  alt?: string;
}

// In-call status the host derives from its voice hook. Kept as a string union so
// the screen can colour the rec dot + pick a hint without importing the hook.
export type VoiceCallStatus =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'blocked'
  | 'error';

export interface VoiceCallScreenProps {
  /**
   * Pre-rendered transcript node — ProfScreen passes the live shared
   * <ChatThread> so the call view shows the EXACT same conversation (text,
   * inline signs, V/F cards, streaming) the chat does, with no duplication.
   * Absent before the first turn, which renders the empty-call placeholder.
   */
  transcript?: ReactNode;
  /**
   * Voice-visuals rebuild P0a — the SINGLE current visual set. Rendered in ONE
   * dedicated slot above the transcript and REPLACED on each new selection (never
   * appended). Empty/undefined → the slot is not rendered. This is what the
   * student is looking at, and the same signs feed the model's currentVisibleImages.
   */
  visualSet?: VoiceCallVisualSign[] | null;
  /** Live in-call status (host derives from its voice hook). */
  status: VoiceCallStatus;
  /** Whether a call is actually engaged (controls timer + status copy). */
  active?: boolean;
  /** Elapsed whole seconds since the call connected (host owns the timer). */
  elapsedSeconds?: number;
  /** Silent-reconnect in progress (policy renewal / transient drop). */
  reconnecting?: boolean;
  /** End the call (circular Stop / FLOW voicecall.back = prof). */
  onStop: () => void;
  /** Interrupt the in-flight Prof turn. Rendered only when provided. */
  onInterrupt?: () => void;
  /** Retry after a blocked/errored call. Rendered only when provided. */
  onRetry?: () => void;
}

/** MM:SS from a whole-second elapsed count. */
function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// In-call status label for the bottom control bar (Italian, in Il Prof's voice).
function callStatusLabel(status: VoiceCallStatus, active: boolean, reconnecting: boolean): string {
  if (!active && status === 'idle') return 'Tocca Chiama e parla';
  switch (status) {
    // Founder decision B: a silent reconnect (policy renewal / transient drop)
    // shows an in-character label so the gap doesn't read as a frozen connection.
    case 'connecting': return reconnecting ? 'Richiamando Il Prof…' : 'Connessione';
    case 'listening':  return 'Sto ascoltando';
    case 'thinking':   return 'Sto ragionando';
    case 'speaking':   return 'Il Prof sta rispondendo';
    case 'blocked':    return 'Microfono bloccato';
    case 'error':      return 'Errore voce';
    default:           return 'Pronta';
  }
}

// Secondary hint under the status line (mirrors the prototype's faint sub-label).
function callStatusHint(status: VoiceCallStatus, active: boolean): string {
  if (!active && status === 'idle') return 'Parla liberamente: non devi premere Invia.';
  switch (status) {
    case 'connecting': return 'Un attimo…';
    case 'thinking':   return 'Sto preparando la risposta';
    case 'speaking':   return 'Parla pure, ti ascolto';
    case 'blocked':    return 'Controlla i permessi del microfono';
    case 'error':      return 'Riprova tra poco';
    default:           return 'Parla liberamente: non devi premere Invia.';
  }
}

export default function VoiceCallScreen({
  transcript,
  visualSet,
  status,
  active = false,
  elapsedSeconds = 0,
  reconnecting = false,
  onStop,
  onInterrupt,
  onRetry,
}: VoiceCallScreenProps): JSX.Element {
  const tokens = useTokens();
  const reducedMotion = useReducedMotion();

  const statusLabel = callStatusLabel(status, active, reconnecting);
  const statusHint = callStatusHint(status, active);
  const callLive = active && status !== 'error' && status !== 'blocked';
  const showInterrupt = Boolean(onInterrupt) && active && (status === 'speaking' || status === 'thinking');
  const showRetry = Boolean(onRetry) && (status === 'blocked' || status === 'error');

  // ── Styles (token-keyed; mockup-verbatim values where tokens don't carry it) ──
  const chromeStyle: CSSProperties = {
    position: 'relative',
    zIndex: 5,
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 60,
    padding: '14px 16px 12px',
    background: tokens.surface,
    borderBottom: `1px solid ${tokens.line}`,
  };

  const recDotStyle: CSSProperties = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: tokens.wrong,
    flexShrink: 0,
    animation: reducedMotion || !callLive ? 'none' : 'rec-blink 1.3s ease-in-out infinite',
  };

  const threadStyle: CSSProperties = {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  };

  const composerStyle: CSSProperties = {
    position: 'relative',
    zIndex: 5,
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    paddingBottom: 'calc(var(--sai-bottom) + 12px)',
    background: tokens.surface,
    borderTop: `1px solid ${tokens.line}`,
  };

  const secondaryBtnStyle: CSSProperties = {
    flexShrink: 0,
    border: `1px solid ${tokens.lineStrong}`,
    borderRadius: 10,
    background: tokens.surface2,
    color: tokens.text,
    minHeight: 44,
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const stopBtnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 60,
    height: 60,
    flexShrink: 0,
    border: '1.5px solid rgba(197, 54, 91, 0.4)',
    borderRadius: '50%',
    background: tokens.surface,
    color: tokens.wrongText,
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(26, 21, 53, 0.06)',
  };

  return (
    <Screen>
      {/* top chrome: Prof identity + live rec dot + MM:SS. V29 S1: the
          hamburger is gone (no app navigation during a call — Stop is the
          single exit, per the call-mode contract). */}
      <div style={chromeStyle}>
        {/* gradient Prof squircle (brand mark) */}
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            minWidth: 40,
            borderRadius: 14,
            background: tokens.brandGradient,
            flexShrink: 0,
          }}
        >
          <svg viewBox="0 0 48 48" width="64%" height="64%" focusable="false">
            <path d="M10 32 Q24 16 38 32" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" fill="none" />
            <circle cx="24" cy="14.5" r="2.6" fill={tokens.spark} />
          </svg>
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <strong style={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.2, color: tokens.text }}>
            Il Prof
          </strong>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.78rem',
              fontWeight: 700,
              color: tokens.wrongText,
            }}
          >
            <span aria-hidden="true" style={recDotStyle} />
            in chiamata ·{' '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatElapsed(elapsedSeconds)}</span>
          </span>
        </div>
      </div>

      {/* Voice-visuals rebuild P0a — the SINGLE current visual set. ONE dedicated
          slot, REPLACED (not appended) each turn: what's here == what the model
          is told is on screen. Empty → not rendered.
          Pilot phone-test (2026-07-07): the strip used to WRAP — 2-3 rows of
          180px signs swallowed the whole phone screen and buried the transcript.
          Now it's ONE horizontally scrollable row of small thumbs; tapping a
          thumb opens the InlineImage lightbox for the big view. */}
      {visualSet && visualSet.length > 0 && (
        <div
          data-testid="voice-visual-set"
          aria-label="Segnali mostrati ora"
          style={{
            flex: '0 0 auto',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            padding: '10px 16px',
            background: tokens.surface,
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          {visualSet.map((sign) => (
            <InlineImage key={sign.id} kind="sign" id={sign.id} altOverride={sign.alt} maxWidth={104} />
          ))}
        </div>
      )}

      {/* live transcript streaming into the thread. When a pre-rendered
          `transcript` node is supplied (ProfScreen's live <ChatThread>), it owns
          the scroll + layout, so we don't double-wrap it in the line container. */}
      {/* live transcript streaming into the thread. ProfScreen supplies the
          live <ChatThread> when the thread has messages; before the first turn
          the placeholder invites the student to speak (the only state the old
          per-line renderer ever actually served — V29 S8, F12). */}
      {transcript !== undefined ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {transcript}
        </div>
      ) : (
        <div style={threadStyle} aria-live="polite">
          <p
            style={{
              margin: 'auto 0',
              textAlign: 'center',
              color: tokens.textMuted,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            La conversazione comparirà qui mentre parli con Il Prof.
          </p>
        </div>
      )}

      {/* call control bar: breathing presence orb + status + compact circular Stop */}
      <div style={composerStyle}>
        <VoiceOrb size="sm" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: tokens.accentInk }}>
            {statusLabel}
          </div>
          <div style={{ fontSize: '0.72rem', color: tokens.textFaint }}>
            {statusHint}
          </div>
        </div>
        {showRetry && (
          <button type="button" style={secondaryBtnStyle} onClick={onRetry} aria-label="Riprova voce AI">
            Riprova
          </button>
        )}
        {showInterrupt && (
          <button type="button" style={secondaryBtnStyle} onClick={onInterrupt} aria-label="Interrompi voce AI">
            Interrompi
          </button>
        )}
        <button
          type="button"
          style={stopBtnStyle}
          onClick={onStop}
          aria-label="Chiudi chiamata vocale"
          title="Termina la chiamata"
        >
          <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={2} focusable="false">
            <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
          </svg>
        </button>
      </div>
    </Screen>
  );
}
